import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// py-components
import { ButtonLoading, ButtonStandard, ModalBase, Text, toast } from "@performyard/py-components";

// components
import { Multiple as MultipleSelectEmployee } from "../../../../../../components/Inputs/SelectEmployee/Multiple";
import type { EmployeeValue } from "../../../../../../components/Inputs/SelectEmployee/shared";

// hooks
import {
	useGoalDetails,
	useGoalHistory,
	useMutateGoalDetails,
	useRbacObjectPermissions,
} from "../../../../../../api/hooks";
import useSearchQueryParams from "../../../../../../hooks/useSearchQueryParams";

// types
import type { Goal } from "../../../../../../api/services";

// utils
import { parseGoalPermissionsForUser } from "../../../utils";

// config
import Config from "../../../config";

type ShareGoalModalProps = {
	goalId?: string;
	isOpen?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
};

const ShareGoalModal = ({
	goalId,
	isOpen: isOpenProp,
	onClose,
	onSuccess,
}: ShareGoalModalProps): React.ReactElement => {
	const { goal_id: goalIdFromParams } = useParams<{ goal_id: string }>();
	const resolvedGoalId = goalId ?? goalIdFromParams ?? "";

	const {
		queryParams: { modal },
		handleQueryChange,
	} = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const { data: permissions } = useRbacObjectPermissions("goal", resolvedGoalId);
	const { userCanShareGoal } = parseGoalPermissionsForUser(permissions);

	const { data, refetch: refetchGoal } = useGoalDetails({ goalID: resolvedGoalId });
	const goal = data ? data.goal : ({} as Goal);

	const { refetch: refetchProgress } = useGoalHistory({
		goalID: resolvedGoalId,
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { refetch: refetchActivity } = useGoalHistory({
		goalID: resolvedGoalId,
		sortDirection: "desc",
		sortName: "_id",
	});

	const isOpen = typeof isOpenProp === "boolean" ? isOpenProp : modal === "share";

	const closeModal = (): void => {
		if (onClose) {
			onClose();
			return;
		}
		handleQueryChange({ modal: undefined });
	};

	const { mutate, isPending } = useMutateGoalDetails(goal?._id, {
		onSuccess: () => {
			refetchGoal();
			refetchProgress();
			refetchActivity();
			onSuccess?.();
			closeModal();
		},
		onError: () => {
			toast.error("Something went wrong, please try again");
		},
	});

	const [selectedEmployees, setSelectedEmployees] = useState<EmployeeValue[]>(
		goal.sharers?.map((sharer) => ({
			id: sharer._id,
			name: sharer.name,
		})) || []
	);

	useEffect(() => {
		if (isOpen) {
			setSelectedEmployees(
				goal.sharers?.map((sharer) => ({
					id: sharer._id,
					name: sharer.name,
				})) || []
			);
		}
	}, [isOpen]);

	const handleShare = (): void => {
		if (!goal?._id) {
			return;
		}
		mutate([
			{
				field: "sharers",
				value: selectedEmployees.map((employee) => employee.id),
			},
		]);
	};

	return (
		<ModalBase isOpen={isOpen} onClose={closeModal} dangerouslySetClassName="w-[432px]">
			<div className="px-6 pb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					Share Goal
				</Text>
				{goal?.name ? (
					<Text size="paragraph-2" weight="regular">
						{goal.name}
					</Text>
				) : null}
			</div>

			<div className="px-6 pb-6">
				<Text size="paragraph-3" weight="semi-bold" dangerouslySetClassName="mb-1">
					Share With
				</Text>
				<MultipleSelectEmployee
					value={selectedEmployees}
					onChange={setSelectedEmployees}
					placeholder="Search employees..."
					data-testid="share-goal-employee-picker"
				/>
			</div>

			<div className="flex justify-center gap-2 px-6 pb-6">
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={closeModal}
						disabled={isPending}
						data-testid="cancel-share-goal"
					>
						Cancel
					</ButtonStandard>
				</div>
				<div className="w-[100px]">
					<ButtonLoading
						color="primary"
						size="large"
						loading={isPending}
						disabled={!userCanShareGoal || !goal?._id || isPending}
						onClick={handleShare}
						data-testid="update-sharers"
					>
						Update
					</ButtonLoading>
				</div>
			</div>
		</ModalBase>
	);
};

export default ShareGoalModal;
